import type { QueryClient } from '@tanstack/react-query';

import { getAuthState } from '@/shared/auth/store';
import { fetchCurrentUser } from '@/shared/auth/session';
import { refreshAccessToken } from '@/shared/auth/token';
import { REALTIME_RECONNECTED, type RealtimeManager } from '@/shared/realtime';

import { companyKeys, invalidateAbsences, invalidateOrg, invalidatePeople } from './api';

/** Realtime events of the identity module (backend `IdentityRealtime`). */
export const COMPANY_EVENTS = {
  userUpdated: 'identity.user-updated',
  departmentChanged: 'identity.department-changed',
  absenceChanged: 'identity.absence-changed',
  invitationsChanged: 'identity.invitations-changed',
} as const;

interface UserChangedPayload {
  userId?: string;
  change?: string;
}

/** What the signed-in user's own session must do when their account changed elsewhere (pure, unit tested). */
export function ownSessionAction(
  payload: UserChangedPayload | null | undefined,
  currentUserId: string | undefined,
): 'refresh-token' | 'reload-user' | null {
  if (!payload?.userId || payload.userId !== currentUserId) return null;
  // New roles live in the access token: get a new one now instead of waiting up to 15 minutes.
  if (payload.change === 'roles') return 'refresh-token';
  if (payload.change === 'updated') return 'reload-user';
  return null;
}

/**
 * Keeps the company caches live: directory and profile changes, the org structure, absences and (for administrators)
 * invitations. After a reconnect everything is refetched because events may have been missed.
 */
export function registerCompanyRealtime(realtime: RealtimeManager, queryClient: QueryClient): () => void {
  const offs = [
    realtime.on<UserChangedPayload | null>(COMPANY_EVENTS.userUpdated, (payload) => {
      invalidatePeople(queryClient);
      const action = ownSessionAction(payload, getAuthState().user?.id);
      if (action === 'refresh-token') void refreshAccessToken();
      else if (action === 'reload-user') void fetchCurrentUser().catch(() => undefined);
    }),
    realtime.on(COMPANY_EVENTS.departmentChanged, () => invalidateOrg(queryClient)),
    realtime.on(COMPANY_EVENTS.absenceChanged, () => invalidateAbsences(queryClient)),
    realtime.on(COMPANY_EVENTS.invitationsChanged, () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.admin });
    }),
    realtime.on(REALTIME_RECONNECTED, () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
}
